import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { CreateSessionRequest, PageDocument, SessionDetail } from '@gn/shared';

/**
 * Device-local draft storage (spec §11). Holds unsynced strokes and revision bookkeeping so writing
 * survives reloads, crashes and network loss. The server is canonical once synced; iOS may evict this
 * storage, which is why we sync eagerly.
 */
export interface LocalSession {
  id: string;
  userId: string;
  create: CreateSessionRequest;
  serverCreated: boolean;
  title: string;
  mode: string;
  updatedAt: number;
  /** Last server view of the session (task prompt, pinned reference) for offline reopening. */
  snapshot?: SessionDetail;
}

export interface LocalPage {
  key: string; // `${sessionId}:${pageId}`
  sessionId: string;
  pageId: string;
  order: number;
  document: PageDocument;
  /** Server revision this local copy is based on. */
  baseRevision: number;
  /** Incremented on every local edit. */
  localVersion: number;
  /** localVersion that the server has acknowledged. */
  syncedVersion: number;
  /** Client revision id reserved for the current localVersion (makes retries idempotent). */
  pendingRid: string | null;
  pendingForVersion: number | null;
  conflict: { serverRevision: number; serverUpdatedAt?: string; serverStrokeCount?: number } | null;
  /** Client revision ids this device uploaded for the page (recognises its own writes on a 409). */
  sentRids?: string[];
  /** Server refused further edits (submitted / exam over). */
  locked: string | null;
  updatedAt: number;
}

interface Schema extends DBSchema {
  sessions: { key: string; value: LocalSession; indexes: { byUser: string } };
  pages: { key: string; value: LocalPage; indexes: { bySession: string } };
}

let dbp: Promise<IDBPDatabase<Schema>> | null = null;

function db() {
  if (!dbp) {
    dbp = openDB<Schema>('greek-notebook', 1, {
      upgrade(d) {
        const s = d.createObjectStore('sessions', { keyPath: 'id' });
        s.createIndex('byUser', 'userId');
        const p = d.createObjectStore('pages', { keyPath: 'key' });
        p.createIndex('bySession', 'sessionId');
      },
    });
  }
  return dbp;
}

export const pageKey = (sessionId: string, pageId: string) => `${sessionId}:${pageId}`;

export const drafts = {
  async getSession(id: string) {
    return (await db()).get('sessions', id);
  },
  async putSession(s: LocalSession) {
    await (await db()).put('sessions', s);
  },
  async sessionsForUser(userId: string) {
    return (await db()).getAllFromIndex('sessions', 'byUser', userId);
  },
  async pages(sessionId: string) {
    const all = await (await db()).getAllFromIndex('pages', 'bySession', sessionId);
    return all.sort((a, b) => a.order - b.order);
  },
  async allPages() {
    return (await db()).getAll('pages');
  },
  async getPage(sessionId: string, pageId: string) {
    return (await db()).get('pages', pageKey(sessionId, pageId));
  },
  async putPage(p: LocalPage) {
    await (await db()).put('pages', p);
  },
  async deletePage(sessionId: string, pageId: string) {
    await (await db()).delete('pages', pageKey(sessionId, pageId));
  },
  async deleteSession(id: string) {
    const d = await db();
    const tx = d.transaction(['sessions', 'pages'], 'readwrite');
    await tx.objectStore('sessions').delete(id);
    const idx = tx.objectStore('pages').index('bySession');
    for await (const cur of idx.iterate(id)) await cur.delete();
    await tx.done;
  },
  async unsyncedCount(): Promise<number> {
    const all = await (await db()).getAll('pages');
    return all.filter((p) => p.localVersion !== p.syncedVersion && !p.locked).length;
  },
  async clearAll() {
    const d = await db();
    await d.clear('pages');
    await d.clear('sessions');
  },
  async estimate(): Promise<{ usage: number; quota: number } | null> {
    try {
      const e = await navigator.storage?.estimate?.();
      return e ? { usage: e.usage ?? 0, quota: e.quota ?? 0 } : null;
    } catch {
      return null;
    }
  },
  /** Ask the browser not to evict our data (best effort; Safari may still evict). */
  async persist(): Promise<boolean> {
    try {
      return (await navigator.storage?.persist?.()) ?? false;
    } catch {
      return false;
    }
  },
};
